import { ApolloClient, ApolloLink } from '@apollo/client';
import { InMemoryCache, NormalizedCacheObject } from '@apollo/client/cache';
import { relayStylePagination } from '@apollo/client/utilities';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { onError } from '@apollo/client/link/error';
import { setContext } from '@apollo/client/link/context';
import rollbar from 'assets/util/rollbar';

let rootPath;
const ENV = process.env.NODE_ENV;

if (ENV === 'development') {
  rootPath = 'http://localhost:3000';
} else if (ENV === 'staging') {
  rootPath = 'https://staging.sumzero.com';
} else if (ENV === 'circle' || ENV === 'test') {
  rootPath = 'http://' + window.location.hostname + ':' + window.location.port;
} else {
  rootPath = window.location.origin;
}

let csrfToken = '';
const authLink = setContext((_, { headers }) => {
  if (!csrfToken) {
    const metaTag = document.querySelector('meta[name="csrf-token"]');
    csrfToken = metaTag?.getAttribute('content') ?? '';
  }

  return {
    headers: {
      ...headers,
      'X-CSRF-TOKEN': csrfToken
    }
  };
});

const mutedErrors = [
  'Error: Response not successful: Received status code 401',
  'TypeError: cancelled',
  'TypeError: Could not connect to the server.',
  'TypeError: Failed to fetch',
  'TypeError: Network request failed',
  'TypeError: NetworkError when attempting to fetch resource.',
  'TypeError: The network connection was lost.',
  'TypeError: Type error',
  'Uncaught TypeError: Illegal invocation',
  'Unexpected token N in JSON at position 0',
  'ServerError: Response not successful: Received status code 422'
];

const mutedGraphQLErrors = [
  'was provided invalid value',
  'User with email already exists in database'
];

const shouldIgnore = (message: string): boolean =>
  mutedGraphQLErrors.find(err => message.includes(err)) ? true : false;

const errorHandler = onError(({ graphQLErrors, networkError, operation }) => {
  if (ENV === 'circle') return;
  if (graphQLErrors) {
    graphQLErrors.map(({ message, locations, path }) =>
      // eslint-disable-next-line no-console
      console.log(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}, OperationName: ${operation.operationName}`
      )
    );

    graphQLErrors
      .filter(({ message }) => !shouldIgnore(message))
      .forEach(error =>
        rollbar.error(
          `[Apollo GraphQL PUBLIC error] Message: ${error.message}, OperationName: ${operation.operationName}`,
          { error, operation }
        )
      );
  }
  if (networkError) {
    // eslint-disable-next-line no-console
    console.log(`[Network error]: ${networkError}, OperationName: ${operation.operationName}`);
    // Mutes failed to fetch errors
    if (mutedErrors.indexOf(String(networkError)) === -1) {
      rollbar.error(
        `[Apollo GraphQL PUBLIC network error] ${networkError}, OperationName: ${operation.operationName}`,
        { error: networkError, operation: operation }
      );
    }
  }
});

const generatePublicClient = (): ApolloClient<NormalizedCacheObject> => {
  return new ApolloClient({
    devtools: {
      enabled: ENV === 'development'
    },
    link: ApolloLink.from([
      authLink,
      errorHandler,
      new BatchHttpLink({
        uri: `${rootPath}/public_queries`,
        credentials: 'same-origin'
      })
    ]),
    cache: new InMemoryCache({
      possibleTypes: {
        FeedItem: ['Idea', 'IdeaUpdate']
      },
      typePolicies: {
        Query: {
          fields: {
            feedItems: relayStylePagination(),
            lettersForFund: relayStylePagination(['fundName']),
            mediaIndexList: relayStylePagination(['indexType', 'type', 'searchParams'])
          }
        }
      }
    })
  });
};

export default generatePublicClient;
